import React, { useState } from 'react';
import { ProviderMatchProviderV9, PMCConfig } from '@kyruus/types';
import {
  injectIntl,
  WrappedComponentProps,
  FormattedMessage
} from '@kyruus/intl';

import CardContainer from '../CardContainer/card-container';
import { HighlightsSectionHeader, CardsContainer } from './styles';
import messages from '../../lib/messages';
import NetworkAffiliations from './network-affiliations';
import Telehealth from './telehealth';
import AgeGroupsSeen from './age-groups-seen';
import Languages from './languages';
import InsurancePlans from './insurance-plans';
import Gender from './gender';

interface ProviderHighlightsProps {
  provider: ProviderMatchProviderV9;
  config: PMCConfig;
  bookOnlineUrl?: string;
  hasAvailability: boolean;
  shouldHidePageAnchors: boolean;
  onBook: (
    event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>
  ) => void;
  shouldSkipHighlightsNavRender: () => void;
}

const ProviderHighlights = ({
  provider,
  config,
  bookOnlineUrl,
  hasAvailability,
  shouldHidePageAnchors,
  onBook,
  shouldSkipHighlightsNavRender,
  intl
}: ProviderHighlightsProps &
  WrappedComponentProps): React.ReactElement | null => {
  const [hasDataInHighlightsCards, setHasDataInHighlightsCards] = useState(
    new Set()
  );

  // Callback to set state when each card renders
  const handleCardsRender = (component: string) => {
    setHasDataInHighlightsCards((prevSet) => new Set(prevSet).add(component));
  };

  // If all highlights data is not present, skip rendering the highlights section
  if (hasDataInHighlightsCards.size === 6) {
    // callback to parent provider-profile component to skip rendering the highlights nav
    shouldSkipHighlightsNavRender();
    return null;
  }
  return (
    <CardContainer>
      <HighlightsSectionHeader
        aria-label={intl.formatMessage(messages.ariaHighlights)}
        id="profile-highlights-section"
      >
        <FormattedMessage {...messages.highlightsHeader} />
      </HighlightsSectionHeader>
      <CardsContainer>
        {/* Each information card decides whether or not it renders */}
        <Telehealth
          provider={provider}
          config={config}
          bookOnlineUrl={bookOnlineUrl}
          hasAvailability={hasAvailability}
          shouldHidePageAnchors={shouldHidePageAnchors}
          onBook={onBook}
          shouldSkipRender={handleCardsRender}
        />
        <AgeGroupsSeen
          provider={provider}
          config={config}
          shouldSkipRender={handleCardsRender}
        />
        <Languages
          provider={provider}
          config={config}
          shouldSkipRender={handleCardsRender}
        />
        <InsurancePlans
          provider={provider}
          config={config}
          shouldHidePageAnchors={shouldHidePageAnchors}
          shouldSkipRender={handleCardsRender}
        />
        <Gender
          provider={provider}
          config={config}
          shouldSkipRender={handleCardsRender}
        />
      </CardsContainer>
      <NetworkAffiliations
        provider={provider}
        config={config}
        shouldSkipRender={handleCardsRender}
      />
    </CardContainer>
  );
};

export default injectIntl(ProviderHighlights);
